import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';

// ============================================================
// Taux de change DZD <-> EUR
// Sources:
//   Officiel  : Banque d'Algerie (via open.er-api.com)
//   Parallele : Square Port-Said (via devisesquare.com)
// Backend scrape les deux sources et les cache 15 min
// ============================================================

const FALLBACK_OFFICIEL = 151.6;
const FALLBACK_PARALLELE = 253;

const CACHE_KEY = 'darna-immo_rates';
const CACHE_DURATION = 15 * 60 * 1000; // 15 min (aligne avec le cache backend)
const MODE_KEY = 'darna-immo_rate_mode'; // 'officiel' | 'parallele'

// Fetcher les taux depuis notre backend
async function fetchRates() {
  // Verifier le cache local d'abord
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      if (Date.now() - data.timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch {}

  let officiel = FALLBACK_OFFICIEL;
  let parallele = FALLBACK_PARALLELE;
  let source = 'fallback';

  try {
    const res = await fetch(`${API_URL}/rates`);
    if (res.ok) {
      const data = await res.json();
      officiel = data.officiel || FALLBACK_OFFICIEL;
      // Le parallele peut etre un objet { achat, vente } ou un nombre
      if (typeof data.parallele === 'object' && data.parallele.achat) {
        parallele = data.parallele.achat; // On utilise le taux d'achat
      } else if (typeof data.parallele === 'number') {
        parallele = data.parallele;
      }
      source = 'api';
    }
  } catch {}

  const rates = { officiel, parallele, source, timestamp: Date.now() };

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(rates));
  } catch {}

  return rates;
}

// ============================================================
// React Context
// ============================================================
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try { return localStorage.getItem(MODE_KEY) || 'parallele'; } catch { return 'parallele'; }
  });
  const [rates, setRates] = useState(() => {
    // Initialiser depuis le cache si disponible
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        return { officiel: data.officiel, parallele: data.parallele };
      }
    } catch {}
    return { officiel: FALLBACK_OFFICIEL, parallele: FALLBACK_PARALLELE };
  });
  const [source, setSource] = useState('cache');

  useEffect(() => {
    fetchRates().then(data => {
      setRates({ officiel: data.officiel, parallele: data.parallele });
      setSource(data.source);
    });
  }, []);

  const setMode = useCallback((m) => {
    setModeState(m);
    try { localStorage.setItem(MODE_KEY, m); } catch {}
  }, []);

  const getRate = useCallback(() => {
    return mode === 'officiel' ? rates.officiel : rates.parallele;
  }, [mode, rates]);

  const dzdToEur = useCallback((dzd) => {
    return Number(dzd) / getRate();
  }, [getRate]);

  const fmtPrix = useCallback((prix, options = {}) => {
    const { showEur = true } = options;
    const num = Number(prix);
    if (isNaN(num) || num === 0) return '0 DZD';

    const dzdStr = num.toLocaleString('fr-DZ') + ' DZD';
    if (!showEur) return dzdStr;

    const eur = dzdToEur(num);
    const eurStr = eur >= 1000
      ? Math.round(eur).toLocaleString('fr-FR') + ' \u20AC'
      : eur.toFixed(0) + ' \u20AC';

    return `${dzdStr} (~${eurStr})`;
  }, [dzdToEur]);

  const value = {
    mode,
    setMode,
    rates,
    source,
    getRate,
    dzdToEur,
    formatPrix: fmtPrix
  };

  return React.createElement(CurrencyContext.Provider, { value }, children);
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency doit etre utilise dans CurrencyProvider');
  return ctx;
}

// ============================================================
// Standalone formatPrix (pour usage hors context)
// ============================================================
function getStoredRate() {
  try {
    const mode = localStorage.getItem(MODE_KEY) || 'parallele';
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      return mode === 'officiel' ? data.officiel : data.parallele;
    }
    return mode === 'officiel' ? FALLBACK_OFFICIEL : FALLBACK_PARALLELE;
  } catch {
    return FALLBACK_PARALLELE;
  }
}

export function formatPrix(prix, options = {}) {
  const { showEur = true } = options;
  const num = Number(prix);
  if (isNaN(num) || num === 0) return '0 DZD';

  const dzdStr = num.toLocaleString('fr-DZ') + ' DZD';
  if (!showEur) return dzdStr;

  const rate = getStoredRate();
  const eur = num / rate;
  const eurStr = eur >= 1000
    ? Math.round(eur).toLocaleString('fr-FR') + ' \u20AC'
    : eur.toFixed(0) + ' \u20AC';

  return `${dzdStr} (~${eurStr})`;
}

export function formatDZD(prix) {
  const num = Number(prix);
  if (isNaN(num) || num === 0) return '0 DZD';
  return num.toLocaleString('fr-DZ') + ' DZD';
}
